import React, { useContext, useState, useEffect, useRef } from "react";
import { Auth } from "aws-amplify";
import {
  Alert,
  Grid,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-react";
import {
  FuseAccountingCentralDatastore,
  ListFixedFeeConfigsCommand,
  SimplifiedFixedFeeConfig,
} from "@amzn/fuse-accounting-central-datastore-client";
import { AuthService } from "src/auth/auth-service";
import FixedFeeConfigTableDisplay from "src/components/allFixedFeeConfigsPage/fixed-fee-configs-table-display";
import { FullPageTitle } from "src/components/header/title";
import { Domain } from "src/config/BartLambdaConfig";
import { getFacdConfig } from "src/config/fuse-accounting-central-datastore-config";
import { InitDomain } from "src/context/domain/domainContext";
import "src/index.scss";

export const AppAllFixedFeeConfigs: React.FC = () => {
  const [authenticating, setAuthenticating] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [payload, setPayload] = useState<any>("init");
  const [ldap, setLdap] = useState("");

  const authObj = useRef(new AuthService());
  const domainData = useContext(InitDomain);
  const facdClient = useRef<any>();

  useEffect(() => {
    const fetchData = async () => {
      // Run authentication
      await authObj.current.authenticate();
      // set loading state to false after authentication completes. This will cause the page to re-render.
      setAuthenticating(false);
      setIsLoggedIn(authObj.current.getIsLoggedIn());
      setLdap(authObj.current.ldap);

      // Load FixedFeeConfig data by default on component mount
      if (authObj.current.getIsLoggedIn()) {
        setPayload("init");
        await getFacdClient(domainData.domain);
        await listFixedFeeConfigs();
      }
    };
    fetchData();
  }, [domainData.domain]);

  const getFacdClient = async (domain: Domain) => {
    const credentials = await Auth.currentCredentials();
    const essentialCredentials = Auth.essentialCredentials(credentials); // Extract essential credentials

    facdClient.current = new FuseAccountingCentralDatastore({
      ...getFacdConfig(domain), // Get the configuration for the domain
      credentials: essentialCredentials, // Pass essential credentials
    });
  };

  const listFixedFeeConfigs = async () => {
    try {
      const listFixedFeeConfigsCommand = new ListFixedFeeConfigsCommand({});

      console.log(
        "Starting FACD listFixedFeeConfigs operation with listFixedFeeConfigsCommand:",
        listFixedFeeConfigsCommand,
      );

      const response = await facdClient.current.send(
        listFixedFeeConfigsCommand,
      );
      console.log("Response received from facdClient:", response);
      const fixedFeeConfigs: SimplifiedFixedFeeConfig[] | undefined =
        response.fixedFeeConfigs;
      setPayload(fixedFeeConfigs);
    } catch (error: any) {
      console.error("An error occurred in listFixedFeeConfigs:", error);
      console.error(error.response);
      setPayload("error");
    } finally {
      console.log("List FixedFeeConfigs operation completed.");
    }
  };

  return (
    <div>
      <FullPageTitle
        title="View All FixedFee Configs"
        description="View all FixedFee Configs and related information."
      />
      <SpaceBetween size="l" />
      {!(ldap.includes("bart-read-only") || ldap.includes("bart-admin")) ? (
        <Grid gridDefinition={[{ colspan: { l: 7, xxs: 10 } }]}>
          <Alert
            header="Only authorized users can access this page!"
            type="error"
          ></Alert>
        </Grid>
      ) : (
        <>
          {payload === "init" && <Spinner size="large" />}
          {payload === "error" && (
            <Grid gridDefinition={[{ colspan: { l: 7, xxs: 10 } }]}>
              <Alert header="Error in search!" type="error"></Alert>
            </Grid>
          )}
          {payload.length === 0 && (
            <Grid gridDefinition={[{ colspan: { l: 7, xxs: 10 } }]}>
              <Alert header="No FixedFee Configs found" type="info"></Alert>
            </Grid>
          )}
          {Array.isArray(payload) && payload.length > 0 && (
            <SpaceBetween direction="vertical" size="m">
              <FixedFeeConfigTableDisplay
                searchResults={payload}
                facdClient={facdClient.current}
                domain={domainData.domain}
              />
            </SpaceBetween>
          )}
        </>
      )}
    </div>
  );
};
